import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Camera, ChevronRight, User, Calendar, MapPin, Heart } from "lucide-react";
import type { TicketWithStatus } from "@/types/api";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import EditTicketInlineModal from "./edit-ticket-inline-modal";
import { usePriorityDisplay } from "@/hooks/use-priorities";
import { useDateFormat } from '@/utils/date-formatter';
import { formatTicketDocumentDateTime } from '@/utils/ticket-document-time';

interface TicketCardProps {
  ticket: TicketWithStatus;
  onClick?: () => void;
  onEditOpen?: () => void;
  onEditClose?: () => void;
}

const statusColors = {
  open: "bg-red-100 text-red-800 border-red-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  closed: "bg-gray-100 text-gray-800 border-gray-200",
} as const;

const priorityColors = {
  low: "bg-blue-100 text-blue-800 border-blue-200",
  medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  critical: "bg-red-100 text-red-800 border-red-200",
} as const;

export default function TicketCard({ ticket, onClick, onEditOpen, onEditClose }: TicketCardProps) {
  const { t, i18n } = useTranslation(['components.ticket']);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const statusKey = (ticket.status as 'open' | 'in_progress' | 'completed' | 'closed') || 'open';
  const statusLabel = ticket.status || '-';
  const statusColor = statusColors[statusKey] || statusColors.open;
  
  // Use priorityId if available, otherwise fallback to priority text
  const { displayName: priorityLabel, color: priorityColorValue } = usePriorityDisplay((ticket as any).priorityId);
  const priorityColor = priorityColorValue 
    ? `bg-[${priorityColorValue}] text-white border-[${priorityColorValue}]`
    : (priorityColors[(ticket.priority as 'low' | 'medium' | 'high' | 'critical') || 'medium'] || priorityColors.medium);
  const hasImages = ticket.images && ticket.images.length > 0;
  const locale = i18n.language?.startsWith('de') ? 'de-DE' : i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const { formatDate } = useDateFormat();
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Like-Funktionalität wurde in den Bearbeitungsdialog verschoben

  const handleEditModalOpen = () => {
    setIsEditModalOpen(true);
    onEditOpen?.();
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    onEditClose?.();
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      handleEditModalOpen();
    }
  };

  return (
    <>
      <Card 
        className="bg-slate-50 border-slate-200 hover:bg-slate-100 transition-colors duration-200 cursor-pointer"
        onClick={handleCardClick}
      >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Status and Priority Badges */}
            <div className="flex items-center gap-3 mb-2">
              <Badge variant="secondary" className={cn("border", statusColor)}>
                {statusLabel}
              </Badge>
              <Badge 
                variant="secondary" 
                className={cn("border", priorityColorValue ? "" : priorityColor)}
                style={priorityColorValue ? { backgroundColor: priorityColorValue, color: 'white', borderColor: priorityColorValue } : undefined}
              >
                {priorityLabel}
              </Badge>
            </div>
            
            {/* Title and Description */}
            <div className="flex items-center gap-2 mb-1">
              {ticket.ticketNumber && (
                <span className="text-xs font-mono text-slate-500">#{ticket.ticketNumber}</span>
              )}
              <h4 className="font-medium text-slate-900 line-clamp-1 text-xs">
                {ticket.title}
              </h4>
            </div>
            <p className="text-slate-600 text-xs mb-2 line-clamp-2">
              {ticket.description}
            </p>
            
            {/* Location */}
            {ticket.location && (
              <div className="flex items-center text-xs text-slate-500 mb-2">
                <MapPin className="h-3 w-3 mr-1" />
                <span>{ticket.location}</span>
              </div>
            )}
            
            {/* Meta Information */}
            <div className="flex items-center text-xs text-slate-500 space-x-3">
              <div className="flex items-center">
                <Calendar className="h-3 w-3 mr-1" />
                <span>
                  {ticket.status === 'completed' && ticket.completedAt
                    ? t('components.ticket:meta.completedOn', { date: formatDate(ticket.completedAt) })
                    : t('components.ticket:meta.createdOn', {
                        date: formatTicketDocumentDateTime(ticket.createdAt!),
                      })}
                </span>
              </div>
              <div className="flex items-center">
                <User className="h-3 w-3 mr-1" />
                <span>
                  {ticket.status === 'completed' || ticket.status === 'in_progress' 
                    ? (ticket.assigneeName || t('components.ticket:meta.notAssigned'))
                    : t('components.ticket:meta.reportedBy', { name: ticket.reporterName })}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Heart className={`h-3 w-3 ${ticket.likes && ticket.likes > 0 ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} />
                <span>{ticket.likes || 0}</span>
              </div>
            </div>
          </div>
          
          {/* Action Icons */}
          <div className="flex flex-col items-end space-y-2 ml-3">
            {/* 🤖 KI-BILDANALYSE BUTTON - IMMER SICHTBAR BEI BILDERN */}
            {hasImages && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Verhindert onClick der Karte
                  console.log('🤖 KI-Analyse für Ticket:', ticket.id, ticket.title);
                  window.open(`/ai-analysis/${ticket.id}`, '_blank');
                }}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded flex items-center gap-1.5 transition-all duration-200 shadow-sm hover:shadow-md"
                title={t('components.ticket:ai.tooltip')}
              >
                🤖 {t('components.ticket:ai.button')}
              </button>
            )}
            
            <div className="flex items-center space-x-2">
              {hasImages && (
                <Camera className="h-3 w-3 text-slate-400" />
              )}
              
              {ticket.status === 'completed' && (
                <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                </div>
              )}
              <ChevronRight className="h-3 w-3 text-slate-400" />
            </div>
          </div>
        </div>
      </CardContent>
      </Card>
      
      {/* Edit Ticket Modal */}
      <EditTicketInlineModal
        ticket={ticket}
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
        }}
        projectId={ticket.projectId}
        planLocation={(ticket as any).planLocation}
      />
    </>
  );
}
